import { PlusCircleIcon, TrashIcon } from '@phosphor-icons/react';
import { Input } from 'antd';
import {
  Controller,
  useFieldArray,
  type Control,
  type FieldPath,
  type UseFormReturn,
} from 'react-hook-form';

import { Radio, RadioGroup } from '@/components/common/radio';
import { useConfirm } from '@/components/feedback/dialog';

import { firstTier, maskDiscountValue, maskInteger, validateTierSet } from '../../schemas';
import type { DiscountFormValues, QuantityRuleFormValue, TierBehavior } from '../../types';

import styles from './discount-create-form.module.scss';

type QuantityType = QuantityRuleFormValue['type'];

/** One tier input cell: the input + (below it) its own error, so an error never shifts sibling
 *  columns. Columns are labelled once by the header row — the input carries an aria-label. */
function TierField({
  control,
  name,
  ariaLabel,
  mask,
  inputMode,
  prefix,
  suffix,
  onBlurExtra,
  computedError,
}: {
  control: Control<DiscountFormValues>;
  name: FieldPath<DiscountFormValues>;
  ariaLabel: string;
  mask: (raw: string) => string;
  inputMode: 'numeric' | 'decimal';
  prefix?: string;
  suffix?: string;
  onBlurExtra?: () => void;
  // Falls back to the pure tier-set validator's own verdict (117111) — RHF's `fieldState.error`
  // is `onTouched`-gated and stays empty for a row the admin never blurred (e.g. one added via
  // "Add tier" and left as-is), which let Create silently disable with no error anywhere on screen.
  computedError?: string;
}) {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const message = fieldState.error?.message ?? computedError;
        return (
          <div className={styles.tierCell}>
            <Input
              id={name}
              aria-label={ariaLabel}
              inputMode={inputMode}
              placeholder="Enter"
              prefix={prefix}
              suffix={suffix}
              status={message ? 'error' : undefined}
              value={typeof field.value === 'string' ? field.value : ''}
              ref={field.ref}
              onBlur={() => {
                field.onBlur();
                onBlurExtra?.();
              }}
              onChange={(e) => field.onChange(mask(e.target.value))}
            />
            {message ? <p className={styles.tierError}>{message}</p> : null}
          </div>
        );
      }}
    />
  );
}

// Figma 4612-1077162 / -1078490: plain radio labels; a single asterisk-prefixed helper line swaps by
// selection (matches ticket 10/11 applicability/trigger).
const TYPE_OPTIONS: { value: QuantityType; label: string }[] = [
  { value: 'fixed', label: 'Fixed Discount' },
  { value: 'quantity_based', label: 'Quantity-Based Discounts' },
];
const TYPE_HELP: Record<QuantityType, string> = {
  fixed: '* Apply the same discount for all quantities.',
  quantity_based: '* Offer different discounts for different quantity ranges.',
};

const BEHAVIOR_OPTIONS: { value: TierBehavior; label: string }[] = [
  { value: 'limit_to_original', label: 'Limit to original discount' },
  { value: 'allow_higher', label: 'Allow higher discounts' },
];

export function QuantityRuleFields({ form }: { form: UseFormReturn<DiscountFormValues> }) {
  const { confirm } = useConfirm();
  const quantityRule = form.watch('quantityRule');
  const discountFormat = form.watch('discountFormat') ?? 'flat_amount';
  const stepValue = form.watch('value');
  const { fields, append, remove, replace } = useFieldArray({
    control: form.control,
    name: 'quantityRule.tiers',
  });

  const isPercentage = discountFormat === 'percentage';
  // Figma: the behaviour helper interpolates the Step-1 value ("$57" / "40%").
  const stepDisplay = isPercentage ? `${stepValue}%` : `$${stepValue}`;
  const valueLabel = isPercentage ? 'Discount Percentage' : 'Discount Amount';

  const setType = (type: QuantityType) => {
    if (type === 'fixed') {
      form.setValue('quantityRule', { type: 'fixed' }, { shouldDirty: true, shouldValidate: true });
      return;
    }
    // Set the discriminant + behaviour, then seed the first tier through the field-array API so
    // useFieldArray's `fields` stay in sync (a bare setValue of the parent object does not sync them).
    form.setValue(
      'quantityRule',
      { type: 'quantity_based', tierBehavior: 'limit_to_original', tiers: [] },
      { shouldDirty: true },
    );
    replace([firstTier(stepValue)]);
  };

  const setBehavior = (tierBehavior: TierBehavior) => {
    form.setValue('quantityRule.tierBehavior', tierBehavior, {
      shouldDirty: true,
      shouldValidate: true,
    });
    // `shouldValidate` re-runs the resolver but RHF only writes back the error for the field it was
    // handed, so a cap error already recorded against a tier row outlives the switch to "Allow higher
    // discounts" and goes on claiming the value is too high (117111 reopen) — and `fieldState.error`
    // outranks the computed verdict in <TierField>. Drop that stale snapshot; the pure validator
    // below re-judges every row under the behaviour just chosen and renders whatever still stands.
    // Deliberately not `trigger()`, which would also stamp "Required" onto rows the admin has not
    // filled yet — the very eagerness the validator was introduced to avoid.
    form.clearErrors('quantityRule.tiers');
  };

  // Append a tier that continues the sequence — new Min = previous Max + 1 (gap-free, no instant
  // overlap), mirroring Figma's 1-10 / 11-20 / 21-30 pattern. Max + value stay empty for the admin.
  const addTier = () => {
    const prevMax = Number(
      fields.length ? form.getValues(`quantityRule.tiers.${fields.length - 1}.maxQty`) : NaN,
    );
    const nextMin = Number.isInteger(prevMax) && prevMax > 0 ? String(prevMax + 1) : '';
    append({ minQty: nextMin, maxQty: '', value: '' });
  };

  // Exceeds-limit popup (no Figma frame — component library; copy flagged to BA): under
  // limit_to_original, a tier value above the Step-1 value raises the dialog on blur.
  const guardTierValue = async (index: number) => {
    if (
      quantityRule.type !== 'quantity_based' ||
      quantityRule.tierBehavior !== 'limit_to_original'
    ) {
      return;
    }
    const raw = form.getValues(`quantityRule.tiers.${index}.value`);
    if (!raw || Number(raw) <= Number(stepValue)) return;
    const switchOption = await confirm({
      title: 'Discount Exceeds the Original Limit',
      subtitle: `This tier value is higher than the original ${stepDisplay} discount. Switch to "Allow higher discounts" to keep it?`,
      confirmText: 'Switch Option',
      cancelText: 'Stay Here',
    });
    // Switch Option → allow higher (clears the cap error). Stay Here → keep the value + inline error.
    if (switchOption) setBehavior('allow_higher');
  };

  // Tier issues computed once from the pure validator — reliable and independent of RHF's
  // `onTouched` error state, which stays empty for a row the admin never blurred (117111). The
  // set-level message (gap/overlap/ascending) renders unconditionally below; per-field messages
  // are looked up by row so an untouched but invalid tier still shows its own error.
  const tierIssues =
    quantityRule.type === 'quantity_based'
      ? validateTierSet(quantityRule.tiers, {
          stepValue,
          tierBehavior: quantityRule.tierBehavior,
        })
      : [];
  const setLevelMessage = tierIssues.find((iss) => iss.path[0] === 'set')?.message;
  const tierFieldError = (index: number, field: 'minQty' | 'maxQty' | 'value') =>
    tierIssues.find((iss) => iss.path[0] === field && iss.path[1] === index)?.message;

  return (
    <fieldset className={styles.applicability}>
      <legend className={styles.sectionLabel}>
        Does quantity affect the discount? <span className={styles.required}>*</span>
      </legend>

      <RadioGroup
        aria-label="Does quantity affect the discount?"
        className={styles.radios}
        value={quantityRule.type}
        onChange={(e) => setType(e.target.value)}
      >
        {TYPE_OPTIONS.map((o) => (
          <Radio key={o.value} value={o.value}>
            {o.label}
          </Radio>
        ))}
      </RadioGroup>

      <p className={styles.helper}>{TYPE_HELP[quantityRule.type]}</p>

      {quantityRule.type === 'quantity_based' ? (
        <div className={styles.quantitySection}>
          <div className={styles.behaviourBlock}>
            <span className={styles.sectionLabel}>
              How should quantity discounts be applied? <span className={styles.required}>*</span>
            </span>
            <RadioGroup
              aria-label="How should quantity discounts be applied?"
              className={styles.radios}
              value={quantityRule.tierBehavior}
              onChange={(e) => setBehavior(e.target.value)}
            >
              {BEHAVIOR_OPTIONS.map((o) => (
                <Radio key={o.value} value={o.value}>
                  {o.label}
                </Radio>
              ))}
            </RadioGroup>
            <p className={styles.helper}>
              {quantityRule.tierBehavior === 'limit_to_original'
                ? `* Cannot exceed the ${stepDisplay} discount.`
                : `* Can exceed the ${stepDisplay} discount.`}
            </p>
          </div>

          <div className={styles.tierTable} role="group" aria-label="Tiers">
            {/* Column labels once, as a header row (Figma 4612-1081210) — the inputs below carry
                matching aria-labels. Keeps every input row uniform so an error never shifts columns. */}
            <div className={styles.tierHeader} aria-hidden>
              <span className={styles.tierColLabel}>
                Min Qty <span className={styles.required}>*</span>
              </span>
              <span className={styles.tierColLabel}>
                Max Qty <span className={styles.required}>*</span>
              </span>
              <span className={styles.tierColLabel}>
                {valueLabel} <span className={styles.required}>*</span>
              </span>
              <span />
            </div>

            {fields.map((f, i) => {
              const base = `quantityRule.tiers.${i}` as const;
              // Only the first (default) tier is protected from deletion (Figma 4612-1081210);
              // every other row, however many exist, gets its own delete icon (117109).
              const showDelete = i > 0;
              return (
                <div key={f.id} className={styles.tierRow}>
                  <TierField
                    control={form.control}
                    name={`${base}.minQty`}
                    ariaLabel="Min Qty"
                    inputMode="numeric"
                    mask={maskInteger}
                    computedError={tierFieldError(i, 'minQty')}
                  />
                  <TierField
                    control={form.control}
                    name={`${base}.maxQty`}
                    ariaLabel="Max Qty"
                    inputMode="numeric"
                    mask={maskInteger}
                    computedError={tierFieldError(i, 'maxQty')}
                  />
                  <TierField
                    control={form.control}
                    name={`${base}.value`}
                    ariaLabel={valueLabel}
                    inputMode="decimal"
                    prefix={isPercentage ? undefined : '$'}
                    suffix={isPercentage ? '%' : undefined}
                    mask={(raw) => maskDiscountValue(raw, discountFormat)}
                    onBlurExtra={() => void guardTierValue(i)}
                    computedError={tierFieldError(i, 'value')}
                  />

                  {showDelete ? (
                    <button
                      type="button"
                      aria-label={`Delete tier ${i + 1}`}
                      className={styles.tierDelete}
                      onClick={() => remove(i)}
                    >
                      <TrashIcon size={16} />
                    </button>
                  ) : null}
                </div>
              );
            })}

            {setLevelMessage ? (
              <p className={styles.error} role="alert">
                {setLevelMessage}
              </p>
            ) : null}

            <button type="button" className={styles.addTier} onClick={addTier}>
              <PlusCircleIcon size={16} />
              Add tier
            </button>
          </div>
        </div>
      ) : null}
    </fieldset>
  );
}
